import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Tooltip } from "@mui/material";
import { SvgIconComponent } from "@mui/icons-material";
import {
  AccountTreeOutlined,
  ApartmentOutlined,
  ArticleOutlined,
  AutoStoriesOutlined,
  ChecklistOutlined,
  EventOutlined,
  FeedOutlined,
  HandshakeOutlined,
  LockOutlined,
  PeopleOutline,
  PersonOutline,
  ShareOutlined,
  SlideshowOutlined,
} from "@mui/icons-material";
import color from "../../../Constants/colors";
import {
  DocumentCraftingLanguage,
  DocumentCraftingRevisionStatus,
  DocumentCraftingRunStatus,
  DocumentCraftingTypeKey,
  DocumentCraftingVisibility,
} from "../../../Models/documentCrafting";
import {
  ErrorState as AmbassadorErrorState,
  StatusChip,
} from "../../Ambassador/Common/AmbassadorUi";
import { useDocumentCraftingEnumLabels } from "./useDocumentCraftingEnumLabels";

// The module shares the page frame pieces of the Ambassador Workspace.
export {
  EmptyState,
  InfoRow,
  LoadingState,
  PageHeader,
  Panel,
  StatGrid,
  StatTile,
  StatusChip,
} from "../../Ambassador/Common/AmbassadorUi";

export type StatusTone = React.ComponentProps<typeof StatusChip>["tone"];

/** Inline load error with "Try again"; the default text is the module's. */
export const ErrorState: React.FC<{ onRetry?: () => void; text?: string }> = ({
  onRetry,
  text,
}) => {
  const { t } = useTranslation("documentCrafting");
  return (
    <AmbassadorErrorState
      onRetry={onRetry}
      text={text ?? t("common.loadError")}
    />
  );
};

const RUN_TONES: Record<DocumentCraftingRunStatus, StatusTone> = {
  [DocumentCraftingRunStatus.QUEUED]: "gray",
  [DocumentCraftingRunStatus.ANALYSING]: "blue",
  [DocumentCraftingRunStatus.AWAITING_ANSWERS]: "orange",
  [DocumentCraftingRunStatus.GENERATING]: "blue",
  [DocumentCraftingRunStatus.RENDERING]: "blue",
  [DocumentCraftingRunStatus.CHECKING]: "blue",
  [DocumentCraftingRunStatus.FIXING]: "blue",
  [DocumentCraftingRunStatus.READY]: "green",
  [DocumentCraftingRunStatus.FAILED]: "red",
  [DocumentCraftingRunStatus.CANCELLED]: "gray",
};

export const RunStatusChip: React.FC<{ status: DocumentCraftingRunStatus }> = ({
  status,
}) => {
  const labels = useDocumentCraftingEnumLabels();
  return (
    <StatusChip
      label={labels.runStatus(status)}
      tone={RUN_TONES[status] ?? "gray"}
    />
  );
};

const REVISION_TONES: Record<DocumentCraftingRevisionStatus, StatusTone> = {
  [DocumentCraftingRevisionStatus.DRAFT]: "orange",
  [DocumentCraftingRevisionStatus.SAVED]: "green",
  [DocumentCraftingRevisionStatus.DISCARDED]: "gray",
  [DocumentCraftingRevisionStatus.EXPIRED]: "gray",
};

export const RevisionStatusChip: React.FC<{
  status: DocumentCraftingRevisionStatus;
}> = ({ status }) => {
  const labels = useDocumentCraftingEnumLabels();
  return (
    <StatusChip
      label={labels.revisionStatus(status)}
      tone={REVISION_TONES[status] ?? "gray"}
    />
  );
};

/** "You have an unsaved draft" marker for lists and headers. */
export const DraftBadge: React.FC = () => {
  const { t } = useTranslation("documentCrafting");
  return <StatusChip label={t("common.myDraft")} tone="orange" />;
};

export const TYPE_ICONS: Record<DocumentCraftingTypeKey, SvgIconComponent> = {
  presentation: SlideshowOutlined,
  onepager_partner: HandshakeOutlined,
  onepager_service: ArticleOutlined,
  flyer: FeedOutlined,
  case_study: AutoStoriesOutlined,
  invitation: EventOutlined,
  checklist_guide: ChecklistOutlined,
  social_post: ShareOutlined,
  diagram: AccountTreeOutlined,
};

/** Icon of a document type, with its name as tooltip. */
export const DocumentTypeIcon: React.FC<{
  typeKey: DocumentCraftingTypeKey;
  size?: number;
  iconColor?: string;
  tooltip?: boolean;
}> = ({ typeKey, size = 20, iconColor = color.gray500, tooltip = true }) => {
  const labels = useDocumentCraftingEnumLabels();
  const Icon = TYPE_ICONS[typeKey] ?? ArticleOutlined;
  const icon = (
    <Icon
      aria-label={labels.type(typeKey)}
      sx={{ fontSize: size, color: iconColor, display: "block" }}
    />
  );
  return tooltip ? (
    <Tooltip title={labels.type(typeKey)} arrow>
      {icon}
    </Tooltip>
  ) : (
    icon
  );
};

/** "DE" / "EN" badge of the document's primary language. */
export const LanguageBadge: React.FC<{ language: DocumentCraftingLanguage }> = ({
  language,
}) => {
  const labels = useDocumentCraftingEnumLabels();
  return (
    <Tooltip title={labels.language(language)} arrow>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          height: 20,
          px: 0.75,
          borderRadius: "6px",
          border: `1px solid ${color.gray300}`,
          color: color.gray700,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.04em",
          lineHeight: 1,
        }}
      >
        {language.toUpperCase()}
      </Box>
    </Tooltip>
  );
};

/** Private, shared with users (pass `shared`), the whole workspace, or a
 * personal document without a workspace (pass `personal`). */
export const VisibilityIcon: React.FC<{
  visibility: DocumentCraftingVisibility;
  shared?: boolean;
  personal?: boolean;
  size?: number;
}> = ({ visibility, shared, personal, size = 18 }) => {
  const { t } = useTranslation("documentCrafting");
  const [Icon, label] = personal
    ? [PersonOutline, t("common.visibilityHint.personal")]
    : visibility === DocumentCraftingVisibility.WORKSPACE
      ? [ApartmentOutlined, t("common.visibilityHint.workspace")]
      : shared
        ? [PeopleOutline, t("common.visibilityHint.shared")]
        : [LockOutlined, t("common.visibilityHint.private")];
  return (
    <Tooltip title={label} arrow>
      <Icon
        aria-label={label}
        sx={{ fontSize: size, color: color.gray500, display: "block" }}
      />
    </Tooltip>
  );
};

/** Small "Personal document" badge: a document without a workspace that only
 * its owner sees. */
export const PersonalBadge: React.FC = () => {
  const { t } = useTranslation("documentCrafting");
  return (
    <Tooltip title={t("common.visibilityHint.personal")} arrow>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          height: 20,
          px: 0.75,
          borderRadius: "6px",
          border: `1px solid ${color.blue200}`,
          backgroundColor: color.blue50,
          color: color.blue700,
          fontSize: 11,
          fontWeight: 700,
          lineHeight: 1,
          whiteSpace: "nowrap",
        }}
      >
        <PersonOutline sx={{ fontSize: 13 }} />
        {t("common.personalDocument")}
      </Box>
    </Tooltip>
  );
};
